/* tslint:disable:variable-name */
import {Injectable, OnDestroy} from '@angular/core';
import {Observable, Subject} from 'rxjs';
import {share, switchMap} from 'rxjs/operators';

export class RefreshConfig {
    refreshTimeout: number; // in seconds
}

/**
 * Page refresh helper service.
 * To be provided by any component using app-refresh in order not to be shared among different components under the same module.
 * Example:
 *   const REFRESH_CONFIG = {
 *     refreshTimeout: 3
 *   };
 *   ...
 *   @Component({
 *     ...
 *     providers: [
 *       { provide: RefreshConfig, useValue: REFRESH_CONFIG },
 *       RefreshService
 *     ]
 *     ...
 *   })
 */
@Injectable()
export class RefreshService implements OnDestroy {
    private _cb;
    private _intHdlr;
    private _subj;
    private _countdown;
    private _busy;
    private _cfg;

    constructor(cfg: RefreshConfig) {
        this._cb = null;
        this._intHdlr = null;
        this._subj = null;
        this._subj = new Subject<void>();
        this._busy = false;
        this._cfg = cfg;
        this._countdown = this._cfg.refreshTimeout;
    }

    get busy(): boolean {
        return this._busy;
    }

    get countdown(): number {
        return this._countdown;
    }

    get isPaused() {
        return this._intHdlr === null;
    }

    ngOnDestroy() {
        if (this._intHdlr) {
            clearInterval(this._intHdlr);
        }
    }

    activate(cb): Observable<any> {
        this._cb = cb;
        const obs = this._subj.pipe(
            switchMap(this._cb),
            // NOTE why we need to share it:
            // https://stackoverflow.com/questions/41172676/switchmap-invokes-inner-function-multiple-times
            share()
        );
        obs.subscribe(() => {
            this._busy = false;
        });
        this._intHdlr = this.mkInterval();
        return obs;
    }

    deactivate(): void {
        if (this._intHdlr) {
            clearInterval(this._intHdlr);
            this._intHdlr = null;
        }
    }

    reactivate(): void {
        // reset timeout
        if (!this._intHdlr) {
            this._countdown = this._cfg.refreshTimeout;
            this._intHdlr = this.mkInterval();
        }
    }

    refresh(): void {
        if (this._cb) {
            this._busy = true;
            this._subj.next();
        }
        this._countdown = this._cfg.refreshTimeout;
    }

    private mkInterval() {
        return setInterval(() => {
            if (--this._countdown <= 0) {
                // countdown is reset by refresh()
                this.refresh();
            }
        }, 1000);
    }
}
